import React, {
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";
import { Grid, Button } from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import HaklaiPreCalcSection from "./preCalc/HaklaiPreCalcSection";
import HaklaiPropertyPartsSection from "./propertyParts/HaklaiPropertyPartsSection";
import HaklaiExpensesSection from "./expenses/HaklaiExpensesSection";
import HaklaiPrisaSection from "./prisa/HaklaiPrisaSection";
import HaklaiAnotherQuestionsSection from "./anotherQuestions/HaklaiAnotherQuestionsSection";
import {
  getHaklaiPrisaMaxYears,
  getHaklaiPrisaMinYears
} from "./prisa/haklaiPrisaHelpers";
import { getHaklaiPrisaSectionState } from "./prisa/haklaiPrisaGates";
import {
  getHaklaiPrisaFieldErrors,
  HAKLAI_PRISA_FIELD_KEYS
} from "./prisa/haklaiPrisaValidation";
import {
  getHaklaiOwnershipStartDate,
  getHaklaiPurchaseDepreciationStartDate
} from "./preCalc/logic/haklaiPreCalcHelpers";
import { buildPropertyPartGroups, buildPropertyPartsComputationState } from "./propertyParts/logic/haklaiPropertyPartsHelpers";
import {
  getExpensesSectionState,
  getHaklaiPreCalcInlineErrors,
  getPropertyPartsPrerequisiteState,
  getPropertyPartsPrerequisiteFieldErrors
} from "./preCalc/logic/haklaiPreCalcValidation";
import useAutoExpandOnUnlock from "./shared/hooks/useAutoExpandOnUnlock";
import {
  HAKLAI_CALC_VIEW_LABELS,
  HAKLAI_CALC_VIEW_MESSAGE_TOKENS,
  hasHaklaiCalcViewMessageToken
} from "./haklaiCalcViewTexts";
import { validateHaklaiPropertyDepreciationProfiles } from "../domain/haklaiValidation";

/**
 * Owns the Haklai calculator screen composition.
 *
 * Responsibilities:
 * - wires the main Haklai sections in flow order
 * - coordinates cross-section validation scroll targets
 * - owns save/analyze button behavior and section expand state
 *
 * Section-specific table rendering and calculation rules should stay inside
 * the relevant feature folder rather than growing this file.
 */

const EMPTY_OBJECT = Object.freeze({});
const ROOT_GRID_STYLE = {
  paddingTop: 20,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  margin: 0,
  boxSizing: "border-box"
};
const SECTION_GRID_ITEM_STYLE = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box"
};
const ACTION_BUTTON_STYLE = { minWidth: "130px" };
const ACTION_BUTTON_ICON_STYLE = { marginRight: "8px" };
const PRE_CALC_ANALYZE_FIELD_ORDER = [
  "soldPropertyDate",
  "totalPropertyCost",
  "wayOfGettingProperty",
  "deduDate",
  "testatorPassingDate",
  "testatorBoughtPropertyDate",
  "amount"
];

const scrollToNamedField = (rootElement, fieldName, occurrenceIndex = 0) => {
  if (!rootElement || !fieldName) return false;
  const fieldElements = rootElement.querySelectorAll(`[name="${fieldName}"]`);
  const fieldElement = fieldElements[Math.max(0, occurrenceIndex)] || fieldElements[0];
  if (!fieldElement) return false;
  const scrollTarget =
    fieldElement.closest(".MuiFormControl-root")
    || fieldElement.closest(".MuiGrid-root")
    || fieldElement;

  scrollTarget.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });

  if (typeof fieldElement.focus === "function") {
    try {
      fieldElement.focus({ preventScroll: true });
    } catch (error) {
      fieldElement.focus();
    }
  }

  return true;
};

const afterNextPaint = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};

const VALIDATION_ALERT_AFTER_SCROLL_MS = 420;

const runAfterValidationScroll = (callback) => {
  if (typeof window === "undefined") {
    callback();
    return;
  }
  window.setTimeout(callback, VALIDATION_ALERT_AFTER_SCROLL_MS);
};

const isPre1997ConstructionValidation = (validationMessage = "") => (
  hasHaklaiCalcViewMessageToken(
    validationMessage,
    HAKLAI_CALC_VIEW_MESSAGE_TOKENS.pre1997Construction
  )
);

const isPropertyDepreciationValidation = (validationMessage = "") => (
  hasHaklaiCalcViewMessageToken(
    validationMessage,
    HAKLAI_CALC_VIEW_MESSAGE_TOKENS.propertyDepreciation
  )
);

const isExactPropertyDepreciationValidation = (
  validationMessage = "",
  propertyDepreciationValidationMessage = ""
) => (
  Boolean(propertyDepreciationValidationMessage)
  && validationMessage === propertyDepreciationValidationMessage
);

const shouldScrollWithExistingAnalyzeMessage = (
  validationMessage = "",
  propertyDepreciationValidationMessage = ""
) => (
  hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.usdRate)
  || isPre1997ConstructionValidation(validationMessage)
  || hasHaklaiCalcViewMessageToken(
    validationMessage,
    HAKLAI_CALC_VIEW_MESSAGE_TOKENS.propertyPartsValueOvershoot
  )
  || isExactPropertyDepreciationValidation(validationMessage, propertyDepreciationValidationMessage)
);

const isLaterThanPropertyDepreciationValidation = (validationMessage = "") => {
  if (!validationMessage) return false;
  if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.expense)) return true;
  if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.grossUp)) return true;
  if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.prisa)) return true;
  return (
    hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.purchase)
    && hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.form11Depreciation)
  );
};

const shouldPrioritizePropertyDepreciationIssue = ({
  validationMessage = "",
  propertyDepreciationValidationMessage = ""
} = {}) => (
  Boolean(propertyDepreciationValidationMessage)
  && (
    isExactPropertyDepreciationValidation(validationMessage, propertyDepreciationValidationMessage)
    || isLaterThanPropertyDepreciationValidation(validationMessage)
  )
);

const getPrisaYearFieldOccurrenceIndex = (prisa = {}, personIndex = 0, yearIndex = 0) => {
  const personsData = Array.isArray(prisa.personsData) ? prisa.personsData : [];
  return personsData.slice(0, personIndex).reduce((sum, person) => (
    sum + (Array.isArray(person?.dataPerYear) ? person.dataPerYear.length : 0)
  ), yearIndex);
};

const getPrisaDomFieldTarget = (fieldKey, prisa = {}) => {
  if (!fieldKey) return null;
  if (fieldKey === HAKLAI_PRISA_FIELD_KEYS.shouldTryPrisa) return { fieldName: "shouldTryPrisa" };
  if (fieldKey === HAKLAI_PRISA_FIELD_KEYS.personsData) return { fieldName: "personsData" };
  if (fieldKey === HAKLAI_PRISA_FIELD_KEYS.isGilumConflict) return { fieldName: "isGilum" };

  const [fieldName, personIndexRaw, yearIndexRaw] = fieldKey.split("_");
  const personIndex = Number(personIndexRaw) || 0;
  const yearIndex = Number(yearIndexRaw) || 0;
  if (fieldName === "birthOfClient") return { fieldName, occurrenceIndex: personIndex };
  if (fieldName === "dataPerYear") return { fieldName, occurrenceIndex: personIndex };
  if (fieldName === "personName") return { fieldName, occurrenceIndex: personIndex };
  if (fieldName === "zikui") return { fieldName, occurrenceIndex: personIndex };
  if (fieldName === "income") {
    return { fieldName, occurrenceIndex: getPrisaYearFieldOccurrenceIndex(prisa, personIndex, yearIndex) };
  }
  return null;
};

const HaklaiCalcView = (props) => {
  const {
    calc,
    acquisitionCostInShekel,
    usdRate,
    usdRateLoading,
    usdRateStale,
    handlePreCalcOnChange,
    handleAcquisitionDetailsOnChange,
    handlePropertyPartOnChange,
    handleAddRow,
    handleRemoveRow,
    handleExpenseOnChange,
    handleAddExpense,
    handleRemoveExpense,
    handlePurchaseDepreciationChange,
    handlePurchaseDepreciationSelectionChange,
    handleMainCalcPrisaOnChange,
    handlePrisaCreditPointsApply,
    handleMainCalcAnotherQuestionsOnChange,
    prisaFieldErrors,
    anotherQuestionsFieldErrors,
    touchPrisaField,
    updateDealData,
    analyzeDeal,
    getAnalyzeValidationError,
    prepareAnalyzeCalc,
    isActionInFlight
  } = props;
  const calcRootRef = useRef(null);
  const [touchedPrerequisiteFields, setTouchedPrerequisiteFields] = useState({});
  const preCalcSectionRef = useRef(null);
  // Imperative handle exposed by HaklaiPropertyPartsSection. Analyze reveals
  // only the first blocking row/field; draft save does not force validation UI.
  const propertyPartsSectionRef = useRef(null);
  // Sibling imperative handle for the expenses table with the same reveal
  // behavior as property parts.
  const expensesSectionRef = useRef(null);
  const preCalc = useMemo(() => calc?.preCalc || EMPTY_OBJECT, [calc?.preCalc]);
  const mainCalc = useMemo(() => calc?.mainCalc || EMPTY_OBJECT, [calc?.mainCalc]);
  const propertyPartsComputationState = useMemo(() => buildPropertyPartsComputationState({
    groups: buildPropertyPartGroups(mainCalc.propertyPartsData || EMPTY_OBJECT),
    totalPropertyCost: preCalc.totalPropertyCost,
    totalAcquisitionCost: acquisitionCostInShekel
  }), [
    acquisitionCostInShekel,
    mainCalc.propertyPartsData,
    preCalc.totalPropertyCost
  ]);

  const propertyPartsState = useMemo(
    () => getPropertyPartsPrerequisiteState(preCalc),
    [preCalc]
  );
  const preCalcFieldErrors = useMemo(
    () => getPropertyPartsPrerequisiteFieldErrors(preCalc, touchedPrerequisiteFields),
    [preCalc, touchedPrerequisiteFields]
  );
  const ownershipStartDate = useMemo(
    () => getHaklaiOwnershipStartDate(preCalc),
    [preCalc]
  );
  const purchaseDepreciationStartDate = useMemo(
    () => getHaklaiPurchaseDepreciationStartDate(preCalc),
    [preCalc]
  );

  const touchPrerequisiteField = useCallback((fieldName) => {
    if (!fieldName) return;
    setTouchedPrerequisiteFields((prev) => (prev[fieldName] ? prev : { ...prev, [fieldName]: true }));
  }, []);

  const handleSaveClick = useCallback(() => {
    updateDealData();
  }, [updateDealData]);

  const expensesState = useMemo(
    () => getExpensesSectionState({
      preCalc,
      propertyPartsData: mainCalc.propertyPartsData || EMPTY_OBJECT,
      totalPropertyCost: preCalc.totalPropertyCost
    }),
    [
      mainCalc.propertyPartsData,
      preCalc
    ]
  );

  const prisaMaxYears = useMemo(() => getHaklaiPrisaMaxYears(preCalc), [preCalc]);
  const prisaMinYears = useMemo(() => getHaklaiPrisaMinYears(preCalc), [preCalc]);
  const prisaSectionState = useMemo(() => getHaklaiPrisaSectionState(preCalc), [preCalc]);
  const showPrisaSection = preCalc.sellerType === "single";
  const prisaLocked = !expensesState.unlocked || !prisaSectionState.unlocked;

  const propertyPartsNotice = useMemo(() => (
    !propertyPartsState.unlocked
      ? { message: propertyPartsState.reason }
      : null
  ), [propertyPartsState.reason, propertyPartsState.unlocked]);

  const expensesNotice = useMemo(() => (
    propertyPartsState.unlocked && !expensesState.unlocked
      ? { message: expensesState.reason }
      : null
  ), [expensesState.reason, expensesState.unlocked, propertyPartsState.unlocked]);
  // Notice cascade: a locked section only shows its yellow banner when
  // it is the *first* disabled section in the progression (all earlier
  // sections are already unlocked). Subsequent locked sections stay
  // greyed/inert but silent, so the user sees exactly one banner at a
  // time pointing to the next thing to fix. This mirrors the existing
  // Expenses rule above (`propertyPartsState.unlocked && !expensesState.unlocked`).
  const prisaNotice = useMemo(() => (
    expensesState.unlocked && !prisaSectionState.unlocked
      ? { message: prisaSectionState.reason }
      : null
  ), [expensesState.unlocked, prisaSectionState.reason, prisaSectionState.unlocked]);

  // AnotherQuestions (שאלות נוספות) is the last section in the chain.
  // It's greyed out until the section immediately before it is unlocked:
  //   - single-seller calcs  → waits on Prisa
  //   - non-single-seller    → Prisa isn't rendered, so waits on Expenses
  // AnotherQuestions has no independent validation gate of its own, so
  // it can never be the "first disabled" section — its notice is always
  // null (passed inline at the callsite). If a future rule adds an
  // own-gate here, compute the notice the same way Prisa does above
  // (fires only when all prior sections are unlocked AND this section's
  // own gate is still closed).
  const anotherQuestionsLocked = showPrisaSection
    ? prisaLocked
    : !expensesState.unlocked;

  // Each section auto-expands exactly once — the first time its
  // prerequisites are satisfied. After that, user-driven collapse/expand
  // is respected. See `shared/hooks/useAutoExpandOnUnlock`.
  const [propertyPartsExpanded, setPropertyPartsExpanded] = useAutoExpandOnUnlock(propertyPartsState.unlocked);
  const [expensesExpanded, setExpensesExpanded] = useAutoExpandOnUnlock(expensesState.unlocked);
  const [prisaExpanded, setPrisaExpanded] = useAutoExpandOnUnlock(!prisaLocked);
  const [anotherQuestionsExpanded, setAnotherQuestionsExpanded] = useAutoExpandOnUnlock(!anotherQuestionsLocked);
  const togglePropertyPartsExpanded = useCallback(() => setPropertyPartsExpanded((prev) => !prev), [setPropertyPartsExpanded]);
  const toggleExpensesExpanded = useCallback(() => setExpensesExpanded((prev) => !prev), [setExpensesExpanded]);
  const togglePrisaExpanded = useCallback(() => setPrisaExpanded((prev) => !prev), [setPrisaExpanded]);
  const toggleAnotherQuestionsExpanded = useCallback(
    () => setAnotherQuestionsExpanded((prev) => !prev),
    [setAnotherQuestionsExpanded]
  );

  const revealFirstPreCalcIssue = useCallback(() => {
    const allErrors = getHaklaiPreCalcInlineErrors(preCalc);
    const firstFieldName = PRE_CALC_ANALYZE_FIELD_ORDER.find((fieldName) => Boolean(allErrors[fieldName]));
    if (!firstFieldName) return false;

    touchPrerequisiteField(firstFieldName);
    preCalcSectionRef.current?.revealField?.(firstFieldName);
    return true;
  }, [preCalc, touchPrerequisiteField]);

  const revealFirstPrisaIssue = useCallback(() => {
    if (!showPrisaSection || prisaLocked) return false;
    const allErrors = getHaklaiPrisaFieldErrors(calc);
    const firstFieldKey = Object.keys(allErrors)[0];
    if (!firstFieldKey) return false;
    const domTarget = getPrisaDomFieldTarget(firstFieldKey, mainCalc.prisa);
    if (!domTarget) return false;

    touchPrisaField(firstFieldKey);
    setPrisaExpanded(true);
    afterNextPaint(() => scrollToNamedField(
      calcRootRef.current,
      domTarget.fieldName,
      domTarget.occurrenceIndex || 0
    ));
    return true;
  }, [
    calc,
    mainCalc.prisa,
    prisaLocked,
    setPrisaExpanded,
    showPrisaSection,
    touchPrisaField
  ]);

  const revealFirstAnotherQuestionsIssue = useCallback(() => {
    if (anotherQuestionsLocked) return false;
    if (!(mainCalc.isGilum && mainCalc.prisa?.shouldTryPrisa)) return false;

    setAnotherQuestionsExpanded(true);
    afterNextPaint(() => scrollToNamedField(calcRootRef.current, "isGilum"));
    return true;
  }, [
    anotherQuestionsLocked,
    mainCalc.isGilum,
    mainCalc.prisa,
    setAnotherQuestionsExpanded
  ]);

  const revealFirstPropertyDepreciationIssue = useCallback(() => {
    if (!propertyPartsState.unlocked) return false;

    const wasExpanded = propertyPartsExpanded;
    const revealIssue = () => (
      propertyPartsSectionRef.current?.scrollToFirstPurchaseDepreciationIssue?.() === true
    );

    setPropertyPartsExpanded(true);
    const didReveal = revealIssue();
    if (didReveal && !wasExpanded && typeof window !== "undefined") {
      window.setTimeout(revealIssue, 320);
    }
    return didReveal;
  }, [
    propertyPartsExpanded,
    propertyPartsState.unlocked,
    setPropertyPartsExpanded
  ]);

  const revealFirstAnalyzeIssue = useCallback(() => {
    if (revealFirstPreCalcIssue()) return true;

    if (propertyPartsState.unlocked) {
      setPropertyPartsExpanded(true);
      if (propertyPartsSectionRef.current?.revealFirstInvalidField?.()) return true;
    }

    if (revealFirstPropertyDepreciationIssue()) return true;

    if (expensesState.unlocked) {
      setExpensesExpanded(true);
      if (expensesSectionRef.current?.revealFirstInvalidField?.()) return true;
    }

    if (revealFirstPrisaIssue()) return true;
    if (revealFirstAnotherQuestionsIssue()) return true;

    return false;
  }, [
    expensesState.unlocked,
    propertyPartsState.unlocked,
    revealFirstAnotherQuestionsIssue,
    revealFirstPreCalcIssue,
    revealFirstPropertyDepreciationIssue,
    revealFirstPrisaIssue,
    setExpensesExpanded,
    setPropertyPartsExpanded
  ]);

  const revealPropertyPartsOvershootIssue = useCallback((computationState = propertyPartsComputationState) => {
    if (!propertyPartsState.unlocked) return false;
    const overshootRowKey = Object.entries(computationState.runningSaleBalanceByKey || {})
      .find(([, balance]) => Number(balance) < 0)?.[0] || "";
    if (!overshootRowKey) return false;

    setPropertyPartsExpanded(true);
    propertyPartsSectionRef.current?.revealValueIssue?.({ rowKey: overshootRowKey });
    return true;
  }, [
    propertyPartsComputationState,
    propertyPartsState.unlocked,
    setPropertyPartsExpanded
  ]);

  const scrollToPropertyPartsFallback = useCallback((preferPurchaseDepreciation = false) => {
    const wasExpanded = propertyPartsExpanded;
    const scrollToPurchaseDepreciationIssue = () => (
      propertyPartsSectionRef.current?.scrollToFirstPurchaseDepreciationIssue?.() === true
    );

    setPropertyPartsExpanded(true);
    afterNextPaint(() => {
      if (
        preferPurchaseDepreciation
        && scrollToPurchaseDepreciationIssue()
      ) {
        if (!wasExpanded && typeof window !== "undefined") {
          window.setTimeout(scrollToPurchaseDepreciationIssue, 320);
        }
        return;
      }
      propertyPartsSectionRef.current?.scrollToSection?.();
    });
    return true;
  }, [propertyPartsExpanded, setPropertyPartsExpanded]);

  const scrollToExpensesFallback = useCallback((preferPurchaseDepreciation = false) => {
    setExpensesExpanded(true);
    afterNextPaint(() => {
      if (
        preferPurchaseDepreciation
        && expensesSectionRef.current?.scrollToFirstPurchaseDepreciationIssue?.()
      ) {
        return;
      }
      expensesSectionRef.current?.scrollToSection?.();
    });
    return true;
  }, [setExpensesExpanded]);

  const scrollToAnalyzeFallback = useCallback((validationMessage = "") => {
    if (!validationMessage) return false;

    const acquisitionDetails = preCalc.acquisitionDetails || {};
    if (acquisitionDetails.deduCurrency === "usd" && !usdRate) {
      preCalcSectionRef.current?.revealField?.("amount");
      return true;
    }

    if (
      propertyPartsState.unlocked
      && !expensesState.unlocked
      && expensesState.blockingSection === "propertyParts"
    ) {
      return scrollToPropertyPartsFallback();
    }

    if (isPre1997ConstructionValidation(validationMessage)) {
      return scrollToPropertyPartsFallback();
    }

    if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.expense)) {
      return scrollToExpensesFallback(false);
    }

    if (
      hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.purchase)
      && hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.form11Depreciation)
    ) {
      return scrollToExpensesFallback(true);
    }

    if (
      hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.propertyPartsDepreciation)
      || isPropertyDepreciationValidation(validationMessage)
    ) {
      return scrollToPropertyPartsFallback(true);
    }

    if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.propertyParts)) {
      return scrollToPropertyPartsFallback();
    }

    if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.grossUp)) {
      setAnotherQuestionsExpanded(true);
      afterNextPaint(() => scrollToNamedField(calcRootRef.current, "isGilum"));
      return true;
    }

    if (hasHaklaiCalcViewMessageToken(validationMessage, HAKLAI_CALC_VIEW_MESSAGE_TOKENS.prisa)) {
      setPrisaExpanded(true);
      afterNextPaint(() => scrollToNamedField(calcRootRef.current, "shouldTryPrisa"));
      return true;
    }

    calcRootRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest"
    });
    return true;
  }, [
    expensesState.blockingSection,
    expensesState.unlocked,
    preCalc.acquisitionDetails,
    propertyPartsState.unlocked,
    scrollToPropertyPartsFallback,
    scrollToExpensesFallback,
    setAnotherQuestionsExpanded,
    setPrisaExpanded,
    usdRate
  ]);

  const handleAnalyzeClick = useCallback(() => {
    const prepareState = prepareAnalyzeCalc?.(calc) || { changed: false, calc };
    const calcForAnalyze = prepareState.calc;
    const propertyPartsComputationStateForAnalyze = buildPropertyPartsComputationState({
      groups: buildPropertyPartGroups(calcForAnalyze.mainCalc?.propertyPartsData || EMPTY_OBJECT),
      totalPropertyCost: calcForAnalyze.preCalc?.totalPropertyCost,
      totalAcquisitionCost: acquisitionCostInShekel
    });

    const runAnalyzeFlow = () => {
      const validationMessage = getAnalyzeValidationError?.(calcForAnalyze) || "";
      if (!validationMessage) {
        analyzeDeal(calcForAnalyze);
        return;
      }
      const propertyDepreciationValidationMessage = validateHaklaiPropertyDepreciationProfiles(calcForAnalyze) || "";
      if (hasHaklaiCalcViewMessageToken(
        validationMessage,
        HAKLAI_CALC_VIEW_MESSAGE_TOKENS.propertyPartsValueOvershoot
      )) {
        if (revealPropertyPartsOvershootIssue(propertyPartsComputationStateForAnalyze)) return;
      }
      if (shouldPrioritizePropertyDepreciationIssue({
        validationMessage,
        propertyDepreciationValidationMessage
      })) {
        if (revealFirstPropertyDepreciationIssue()) {
          return;
        }
      }
      if (validationMessage && shouldScrollWithExistingAnalyzeMessage(
        validationMessage,
        propertyDepreciationValidationMessage
      )) {
        scrollToAnalyzeFallback(validationMessage);
        runAfterValidationScroll(() => analyzeDeal(calcForAnalyze));
        return;
      }

      if (revealFirstAnalyzeIssue()) return;
      scrollToAnalyzeFallback(validationMessage);
      runAfterValidationScroll(() => analyzeDeal(calcForAnalyze));
    };

    if (prepareState.changed) {
      afterNextPaint(runAnalyzeFlow);
      return;
    }
    runAnalyzeFlow();
  }, [
    acquisitionCostInShekel,
    analyzeDeal,
    calc,
    getAnalyzeValidationError,
    prepareAnalyzeCalc,
    revealPropertyPartsOvershootIssue,
    revealFirstAnalyzeIssue,
    revealFirstPropertyDepreciationIssue,
    scrollToAnalyzeFallback
  ]);

  return (
    <Grid
      ref={calcRootRef}
      container
      direction="column"
      spacing={2}
      style={ROOT_GRID_STYLE}
    >
      <Grid item style={SECTION_GRID_ITEM_STYLE}>
        <HaklaiPreCalcSection
          ref={preCalcSectionRef}
          soldPropertyDate={preCalc.soldPropertyDate}
          totalPropertyCost={preCalc.totalPropertyCost}
          hasZaban={preCalc.hasZaban}
          wayOfGettingProperty={preCalc.wayOfGettingProperty}
          acquisitionDetails={preCalc.acquisitionDetails}
          acquisitionCostInShekel={acquisitionCostInShekel}
          usdRate={usdRate}
          usdRateLoading={usdRateLoading}
          usdRateStale={usdRateStale}
          handlePreCalcOnChange={handlePreCalcOnChange}
          handleAcquisitionDetailsOnChange={handleAcquisitionDetailsOnChange}
          fieldErrors={preCalcFieldErrors}
          handlePrerequisiteFieldBlur={touchPrerequisiteField}
        />
      </Grid>

      <Grid item style={SECTION_GRID_ITEM_STYLE}>
        <HaklaiPropertyPartsSection
          ref={propertyPartsSectionRef}
          wayOfGettingProperty={preCalc.wayOfGettingProperty}
          acquisitionDetails={preCalc.acquisitionDetails}
          propertyPartsData={mainCalc.propertyPartsData}
          totalPropertyCost={preCalc.totalPropertyCost}
          totalAcquisitionCost={acquisitionCostInShekel}
          soldPropertyDate={preCalc.soldPropertyDate}
          purchaseDepreciationStartDate={purchaseDepreciationStartDate}
          residentialRentalByPart={mainCalc.residentialRentalByPart}
          purchaseDepreciationByPart={mainCalc.purchaseDepreciationByPart}
          hasZaban={preCalc.hasZaban}
          ptorType={preCalc.ptorType}
          hasTzinunOnPtor={preCalc.hasTzinunOnPtor}
          handlePreCalcOnChange={handlePreCalcOnChange}
          handlePropertyPartOnChange={handlePropertyPartOnChange}
          handleAddRow={handleAddRow}
          handleRemoveRow={handleRemoveRow}
          handlePurchaseDepreciationChange={handlePurchaseDepreciationChange}
          locked={!propertyPartsState.unlocked}
          lockNotice={propertyPartsNotice}
          expanded={propertyPartsExpanded}
          onToggleExpanded={togglePropertyPartsExpanded}
        />
      </Grid>

      <Grid item style={SECTION_GRID_ITEM_STYLE}>
        <HaklaiExpensesSection
          ref={expensesSectionRef}
          expenses={mainCalc.expenses}
          propertyPartsData={mainCalc.propertyPartsData}
          hasZaban={preCalc.hasZaban}
          preCalc={preCalc}
          soldPropertyDate={preCalc.soldPropertyDate}
          ownershipStartDate={ownershipStartDate}
          purchaseDepreciationStartDate={purchaseDepreciationStartDate}
          residentialRentalByPart={mainCalc.residentialRentalByPart}
          purchaseDepreciationByPart={mainCalc.purchaseDepreciationByPart}
          purchaseDepreciationSelectionByPart={mainCalc.purchaseDepreciationSelectionByPart}
          handleExpenseOnChange={handleExpenseOnChange}
          handleAddExpense={handleAddExpense}
          handleRemoveExpense={handleRemoveExpense}
          handlePurchaseDepreciationChange={handlePurchaseDepreciationChange}
          handlePurchaseDepreciationSelectionChange={handlePurchaseDepreciationSelectionChange}
          locked={!expensesState.unlocked}
          lockNotice={expensesNotice}
          expanded={expensesExpanded}
          onToggleExpanded={toggleExpensesExpanded}
        />
      </Grid>

      {showPrisaSection ? (
        <Grid item style={SECTION_GRID_ITEM_STYLE}>
          <HaklaiPrisaSection
            prisa={mainCalc.prisa}
            soldPropertyDate={preCalc.soldPropertyDate}
            maxYears={prisaMaxYears}
            minYears={prisaMinYears}
            handlePrisaOnChange={handleMainCalcPrisaOnChange}
            handlePrisaCreditPointsApply={handlePrisaCreditPointsApply}
            locked={prisaLocked}
            lockNotice={prisaNotice}
            expanded={prisaExpanded}
            onToggleExpanded={togglePrisaExpanded}
            fieldErrors={prisaFieldErrors}
            onFieldBlur={touchPrisaField}
          />
        </Grid>
      ) : null}

      <Grid item style={SECTION_GRID_ITEM_STYLE}>
        <HaklaiAnotherQuestionsSection
          kizuzIski={mainCalc.kizuzIski}
          kizuzHoni={mainCalc.kizuzHoni}
          isGilum={mainCalc.isGilum}
          afkaa={mainCalc.afkaa}
          handleOnChange={handleMainCalcAnotherQuestionsOnChange}
          fieldErrors={anotherQuestionsFieldErrors}
          expanded={anotherQuestionsExpanded}
          onToggleExpanded={toggleAnotherQuestionsExpanded}
          locked={anotherQuestionsLocked}
        />
      </Grid>

      <Grid item container direction="row" spacing={2} style={SECTION_GRID_ITEM_STYLE}>
        <Grid item>
          <Button
            style={ACTION_BUTTON_STYLE}
            endIcon={<PlayArrowIcon style={ACTION_BUTTON_ICON_STYLE} />}
            variant="contained"
            color="secondary"
            onClick={handleAnalyzeClick}
            disabled={isActionInFlight}
          >
            {HAKLAI_CALC_VIEW_LABELS.analyzeDeal}
          </Button>
        </Grid>
        <Grid item>
          <Button
            style={ACTION_BUTTON_STYLE}
            endIcon={<SaveIcon style={ACTION_BUTTON_ICON_STYLE} />}
            variant="contained"
            color="secondary"
            onClick={handleSaveClick}
            disabled={isActionInFlight}
          >
            {HAKLAI_CALC_VIEW_LABELS.save}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default React.memo(HaklaiCalcView);
